//---------------------------------------------------------------------------------------------------
//  ?                              USE OPEN TRADE LEVELS
//  @author         :  DeeTrade Team
//  @createdOn      :  07/23/2026
//  @description    :  Fetches the CURRENTLY-OPEN trade(s) for a strategy instance — per-account
//                     groups, each with its live SL/TP bracket-leg edge + per-bar level_history —
//                     from the durable `GET /order-levels/open-trade` lane. Sibling to
//                     useOrderLevelsHistory; the source that hydrates the chart SL/TP band on
//                     mount / reload / reconnect so it survives WS trade_context silence.
//                     Exposes { openTrades (ARRAY), status, refetch }.
//---------------------------------------------------------------------------------------------------

import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';

//--> Sibling to useOrderLevelsHistory (list-of-account-groups vs list-of-50-closed).
//*   account_id / execution_mode are NOT sent — the endpoint DERIVES them server-side and
//*   returns them as per-account attribution (the FE has no source for them; see fix-design D3/D6).
export function useOpenTradeLevels(strategyInstanceId, { instrumentId, mode } = {}) {
    const [openTrades, setOpenTrades] = useState([]);
    //! status is DISTINCT from `openTrades` being empty: 'loading' | 'ok' | 'error'. A fetch error
    //!  KEEPS the last-good band (stale-but-present) instead of clearing to naked — a transient
    //!  500/timeout must not read as "no SL/TP" on a risk display.
    const [status, setStatus] = useState('ok');

    const fetchOnce = useCallback(async () => {
        //--> Backtest short-circuit (D6): backtest deployments have no `orders` rows — never fetch.
        //*   DeploymentMode values are lowercase (strategy.py); the API serializes the value.
        if (mode === 'backtest') { setOpenTrades([]); setStatus('ok'); return; }
        //--> Null-instrument short-circuit (AC-2): instrument_id is a REQUIRED query param, so a null
        //*   would 422 on every render. No fetch; honest-empty (status 'ok', not 'error') — no 422 spam.
        if (!strategyInstanceId || !instrumentId) { setOpenTrades([]); setStatus('ok'); return; }
        setStatus('loading');
        try {
            const resp = await api.get('/order-levels/open-trade', {
                params: { strategy_instance_id: strategyInstanceId, instrument_id: instrumentId },
            });
            const payload = resp?.data?.data || {};
            //--> rev4: open_trades is an ARRAY (one element per account group; [] = honest-naked).
            setOpenTrades(Array.isArray(payload.open_trades) ? payload.open_trades : []);
            setStatus('ok');
        } catch (err) {
            //! Distinct fetch-failure state (AC-2): KEEP the last-good openTrades (do NOT clear).
            //!  Contrast the terminal-clear path, which clears a group only on a genuine SUCCESS
            //!  response that no longer contains it. [DIAG]-prefixed warn per feedback_diag_prefix.
            // eslint-disable-next-line no-console
            console.warn('[DIAG] useOpenTradeLevels fetch failed; keeping last-good band', err?.message || err);
            setStatus('error');
        }
    }, [strategyInstanceId, instrumentId, mode]);

    useEffect(() => {
        fetchOnce();
    }, [fetchOnce]);

    return { openTrades, status, refetch: fetchOnce };
}

export default useOpenTradeLevels;
